import { IElementClickedEventPayload } from 'state/analytics/events';
import { EventNames } from 'state/analytics/types';

import { PLATFORM } from './constants';
import { ElementType, IAdobeEvent } from './types';

const createAdobeEvent = <T extends Record<string, any>>({
  eventName,
  loyaltyId,
  payload,
}: {
  eventName: EventNames;
  loyaltyId: string | null;
  payload: T;
}): IAdobeEvent<T> => {
  return {
    event: eventName,
    _timhortons: {
      platform: PLATFORM,
      loyalty: {
        id: loyaltyId,
      },
    },
    ...payload,
  };
};

interface IAdobeProductCategory {
  categoryName: string;
}
export interface IAdobeProductItem {
  SKU: string;
  name: string;
  priceTotal?: number;
  productCategories?: IAdobeProductCategory[];
  quantity: number;
}

interface IAdobeMarketingData {
  campaignGroup: string | null;
  campaignName: string | null;
  trackingCode: string | null;
  _timhortons: {
    trackingTerm: string | null;
    trackingContent: string | null;
  };
}
export interface IAdobePurchaseEventPayload {
  commerce: {
    order: {
      priceTotal: number;
      currency: string;
      purchaseID: string;
    };
  };
  marketing?: IAdobeMarketingData;
  productListItems: IAdobeProductItem[];
}

export const adobePurchaseEvent = ({
  payload,
  loyaltyId,
}: {
  payload: IAdobePurchaseEventPayload;
  loyaltyId: string | null;
}): IAdobeEvent<IAdobePurchaseEventPayload> => {
  return createAdobeEvent({ eventName: EventNames.PURCHASE, payload, loyaltyId });
};

export const adobeElementClickedEvent = ({
  payload,
  loyaltyId,
}: {
  payload: IElementClickedEventPayload;
  loyaltyId: string | null;
}) => {
  return createAdobeEvent({
    eventName: EventNames.ELEMENT_CLICKED,
    payload: { web: { ...payload, type: ElementType.BUTTON } },
    loyaltyId,
  });
};

interface IAdobeItemViewedEventPayload {
  id: string;
  name: string;
}

export const adobeItemViewedEvent = ({
  payload,
  loyaltyId,
}: {
  payload: IAdobeItemViewedEventPayload;
  loyaltyId: string | null;
}) => {
  return createAdobeEvent({
    eventName: EventNames.ITEM_VIEWED,
    payload: {
      commerce: {
        productViews: payload,
      },
    },
    loyaltyId,
  });
};

interface IAdobeItemAddedEventPayload {
  id: string;
  cartId: string;
  quantity: number;
  name: string;
}

export const adobeItemAddedEvent = ({
  payload,
  loyaltyId,
}: {
  payload: IAdobeItemAddedEventPayload;
  loyaltyId: string | null;
}) => {
  return createAdobeEvent({
    eventName: EventNames.ITEM_ADDED,
    payload: {
      commerce: {
        cart: {
          cartID: payload.cartId,
        },
        productListAdds: {
          id: payload.id,
          value: payload.quantity,
          name: payload.name,
        },
      },
    },
    loyaltyId,
  });
};

interface IAdobeItemRemovedEventPayload {
  id: string;
  cartId: string;
  quantity: number;
  name: string;
}

export const adobeItemRemovedEvent = ({
  payload,
  loyaltyId,
}: {
  payload: IAdobeItemRemovedEventPayload;
  loyaltyId: string | null;
}) => {
  return createAdobeEvent({
    eventName: EventNames.ITEM_REMOVED,
    payload: {
      commerce: {
        cart: {
          cartID: payload.cartId,
        },
        productListRemovals: {
          id: payload.id,
          value: payload.quantity,
          name: payload.name,
        },
      },
    },
    loyaltyId,
  });
};
